/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useState } from "react";
import { MediaBatchPreparationInitServices } from "../../../../../../service/mediaBatchPrepInitServices";
import type {
	MediaPowderBatches,
	MediaSolutionBatches,
	PowderLotDetail,
	SolutionLotDetail,
	SolutionPowderMixingPayload,
} from "../../../types/mediaBatchPreparation.types";
import { useTranslation } from "react-i18next";
import { MediaBatchData } from "../../../types/mediaBatchCommon.types";

const formatDateTimeForAPI = (dateTimeLocal: string): string => {
	return dateTimeLocal.replace("T", " ");
};


const preparePayload = (
	mixingParameters: SolutionPowderMixingPayload,
	mediaBatchData: MediaBatchData,
): any => {
	return {
		media_batch_id: mediaBatchData.id,
		powder_batch_id: Number(mixingParameters.powder_batch_id),
		powder_lot_id: Number(mixingParameters.powder_lot_id),
		powder_quantity_consumed: Number(mixingParameters.powder_quantity_consumed),
		solution_batch_id: Number(mixingParameters.solution_batch_id),
		solution_lot_id: Number(mixingParameters.solution_lot_id),
		media_master_id: Number(mediaBatchData.media_master_id),
		intended_use_id: Number(mediaBatchData.intended_use_id),
		solution_volume_used: Number(mixingParameters.solution_volume_used),
		process_start_time: formatDateTimeForAPI(mixingParameters.process_start_time),
		process_end_time: formatDateTimeForAPI(mixingParameters.process_end_time),
		stirring_duration_minutes: Number(mixingParameters.stirring_duration_minutes),
		agitation_rpm: Number(mixingParameters.agitation_rpm),
		observed_ph: Number(mixingParameters.observed_ph),
		process_remarks: mixingParameters.process_remarks,
		has_deviation: mixingParameters.has_deviation,
		deviation_remarks: mixingParameters.deviation_remarks,
		additives: mixingParameters.additives.map(({ batch_id, lot_id, quantity }) => ({
			batch_id: Number(batch_id),
			lot_id: Number(lot_id),
			quantity: Number(quantity),
		})),
		reagents: mixingParameters.reagents.map(({ batch_id, lot_id, quantity }) => ({
			batch_id: Number(batch_id),
			lot_id: Number(lot_id),
			quantity: Number(quantity),
		})),
	};
};

interface Props {
	mixingParameters: SolutionPowderMixingPayload;
	showToast: (message: string, toastType: "success" | "error") => void;
	selectedPowderBatch: MediaPowderBatches | null;
	selectedSolutionBatch: MediaSolutionBatches | null;
	selectedPowderLot: PowderLotDetail | null;
	selectedSolutionLot: SolutionLotDetail | null;
	mediaBatchData: MediaBatchData | null;
	goNext: () => void;
	goBack: () => void;
	validateFields: () => boolean;
}

const MPFooter = ({
	mediaBatchData,
	mixingParameters,
	showToast,
	selectedPowderBatch,
	selectedPowderLot,
	selectedSolutionBatch,
	selectedSolutionLot,
	goNext,
	goBack,
	validateFields
}: Props) => {
	const { t } = useTranslation();

	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmit = async () => {
		if (!mediaBatchData) return;
		if (mediaBatchData.status !== "Draft") {
			goNext();
			return;
		}
		if (!validateFields()) return;

		setIsSubmitting(true);
		const payload = preparePayload(mixingParameters, mediaBatchData);
		try {
			const response =
				await MediaBatchPreparationInitServices.postPowderSolutionMixing(
					payload,
				);

			console.log("Powder mix: ", {
				...payload,
				selectedPowderBatch,
				selectedPowderLot,
				selectedSolutionBatch,
				selectedSolutionLot,
				process_id: response.data.process_id,
			});

			showToast(
				response?.message ||
				"Media Preparation Saved Successfully",
				"success",
			);

			setTimeout(() => {
				goNext();
			}, 2500);

		} catch (error: any) {

			showToast(
				error?.response?.data?.message ||
				"An unexpected error occurred",
				"error",
			);

		} finally {
		setIsSubmitting(false);
	}
	};
	return (
		<div className="bg-white border-t border-slate-200 px-10 py-2 flex items-center justify-between shrink-0 shadow-[0_-20px_40px_-20px_rgba(0,0,0,0.05)]">
			<button
				onClick={goBack}
				className="flex items-center gap-2 px-8 py-3.5 text-[11px] font-black text-slate-500 uppercase tracking-widest hover:text-red-500 transition-all border-none rounded-2xl"
			>
				<span className="material-symbols-outlined text-sm">
					arrow_back
				</span>
				{t("mediaBatchInit.preparation.backToBatch")}
			</button>

			<div className="flex items-center gap-12">
				<div className="flex items-center gap-3">
					<span className="material-symbols-outlined text-emerald-500 font-black">
						verified_user
					</span>
					<div className="flex flex-col">
						<span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] leading-none mb-1">
							{t("mediaBatchInit.preparation.complianceStatus")}
						</span>
						<span className="text-[10px] font-black text-emerald-600 uppercase tracking-wider">
							{t("mediaBatchInit.preparation.secureEntryModeActive")}
						</span>
					</div>
				</div>
				{/* <button className="text-[11px] font-black text-slate-400 uppercase tracking-widest hover:text-red-500 transition-colors">
					{t("mediaBatchInit.preparation.discardDraft")}
				</button> */}
				<button
					disabled={isSubmitting}
					onClick={handleSubmit}
					className="bg-[#FF7043] text-white flex items-center text-[11px] gap-2 px-6 py-2.5 rounded-2xl shadow-xl shadow-orange-500/30 font-black uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all border-none disabled:opacity-50"
				>
					{isSubmitting
						? t("mediaBatchInit.preparation.submitting")
						: t("mediaBatchInit.preparation.proceedToSterilization")}
					<span className="material-symbols-outlined">arrow_forward</span>
				</button>
			</div>
		</div>
	);
};

export default MPFooter;
